/* =====================================================================
 * DM API Client — DM 관련 REST API + WebSocket 헬퍼
 * ===================================================================== */

import type {
  DMAgentType,
  DMMessagesResponse,
  DMUnreadResponse,
  DMSettings,
} from '@auw/workspace-utils/types/dm';
import { authFetch, getHeaders, API_BASE_URL } from './authFetch';

/* ------------------------------------------------------------------ */
/*  메시지 조회                                                        */
/* ------------------------------------------------------------------ */

/** 특정 에이전트와의 메시지 목록을 조회합니다. */
export async function getDMMessages(
  agentType: DMAgentType,
  limit = 50,
  before?: string,
): Promise<DMMessagesResponse> {
  const params = new URLSearchParams({
    agent_type: agentType,
    limit: String(limit),
  });
  if (before) params.set('before', before);

  const url = `${API_BASE_URL}/api/v1/dm/messages?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`DM messages fetch failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 에이전트별 읽지 않은 메시지 수를 조회합니다. */
export async function getDMUnreadCounts(): Promise<DMUnreadResponse> {
  const url = `${API_BASE_URL}/api/v1/dm/messages/unread`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`DM unread fetch failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  읽음 처리                                                          */
/* ------------------------------------------------------------------ */

/** 메시지를 읽음 처리합니다. */
export async function markDMRead(
  messageIds: string[],
): Promise<{ success: boolean; updated_count: number }> {
  const url = `${API_BASE_URL}/api/v1/dm/messages/read`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ message_ids: messageIds }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`DM read mark failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 특정 에이전트의 모든 안읽은 메시지를 읽음 처리합니다. */
export async function markAllReadByAgent(
  agentType: DMAgentType,
): Promise<{ success: boolean; updated_count: number }> {
  const url = `${API_BASE_URL}/api/v1/dm/messages/read/${agentType}`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`DM agent read mark failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  DM 설정                                                            */
/* ------------------------------------------------------------------ */

/** DM 설정을 조회합니다. */
export async function getDMSettings(): Promise<DMSettings> {
  const url = `${API_BASE_URL}/api/v1/dm/settings`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`DM settings fetch failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** DM 설정을 업데이트합니다. */
export async function updateDMSettings(
  updates: Partial<DMSettings>,
): Promise<DMSettings> {
  const url = `${API_BASE_URL}/api/v1/dm/settings`;
  const res = await authFetch(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`DM settings update failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  WebSocket URL 생성                                                 */
/* ------------------------------------------------------------------ */

/**
 * DM WebSocket URL 을 생성한다.
 *
 * 서버는 first-message 인증만 허용하므로 쿼리 파라미터로 토큰을 붙이지 않는다.
 * JWT 는 handshake 직후 `{ type: 'auth', data: { token } }` 메시지로 전송한다.
 */
export function getDMWebSocketUrl(): string {
  const wsBase = API_BASE_URL.replace(/^http/, 'ws');
  return `${wsBase}/api/v1/dm/ws`;
}
